// Texte lu par le bouton « Écouter » : une réponse Markdown devient un texte prononçable
// par la synthèse vocale fr-FR. Chaque ligne (titre, puce, ligne de tableau…) se termine
// par une pause ; un tableau est lu ligne par ligne, « colonne : valeur » ; code, formules,
// liens et emoji sont annoncés ou retirés ; unités et nombres sont écrits comme on les dit
// (« 13 120 500 FCFA » → « 13120500 francs CFA », « 12,5 % » → « 12,5 pour cent »,
// « 10,4 L/100 km » → « 10,4 litres aux 100 kilomètres »).
// Aucune assertion arrière (lookbehind) dans les expressions : Safari < 16.4 ne les lit pas.

import { CALLOUT_LABELS } from './callouts.js';

const FENCE_RE = /^ {0,3}(`{3,}|~{3,})/;
const QUOTE_RE = /^ {0,3}>[ \t]?/;
const CALLOUT_RE = /^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\][ \t]*/i;
const HEADING_RE = /^ {0,3}#{1,6}(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/;
// Soulignement d'un titre (« ===== », « ----- ») ou filet horizontal (« *** », « - - - »)
const UNDERLINE_RE = /^ {0,3}(?:=+|-+)[ \t]*$/;
const RULE_RE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const ITEM_RE = /^[ \t]*(?:[-*+•◦‣⁃]|(\d{1,9})[.)])[ \t]+(.*)$/;
const TASK_RE = /^\[[ xX]\][ \t]+/;
// Ligne entièrement en gras (« **Synthèse** ») : faux titre, lu comme un titre
const BOLD_LINE_RE = /^(\*\*|__)(.+?)\1[ \t]*(:?)[ \t]*$/;
const DELIMITER_RE = /^[ \t]*\|?[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/;

// Pictogrammes retirés : emoji (modificateurs, drapeaux, séquences comprises), coches, étoiles
const PICTOGRAM_RE = /[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}\u{1F3FB}-\u{1F3FF}\u{E0020}-\u{E007F}\u{200D}\u{FE0E}\u{FE0F}\u{20E3}\u{2605}\u{2606}\u{2610}\u{2612}\u{2713}\u{2717}\u{2718}]/gu;
const CODE_BLOCK = '(bloc de code)';
const EMPTY_CELL_RE = /^(?:—|–|-|n\/a)$/i;

const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const PER = { km: 'kilomètre', l: 'litre', h: 'heure', mois: 'mois', jour: 'jour', j: 'jour', an: 'an', semaine: 'semaine', véhicule: 'véhicule' };
const MULTIPLIERS = { k: ['mille', 'mille'], K: ['mille', 'mille'], M: ['million de', 'millions de'], Md: ['milliard de', 'milliards de'], Mds: ['milliard de', 'milliards de'] };

const unquote = (line) => {
  let rest = line;
  while (QUOTE_RE.test(rest)) rest = rest.replace(QUOTE_RE, '');
  return rest;
};

// Règle française : singulier en dessous de 2 (« 1,5 litre », « 2 litres »)
const isSingular = (number) => Math.abs(parseFloat(String(number).replace(',', '.'))) < 2;
const withUnit = (number, singular, plural) => `${number} ${isSingular(number) ? singular : plural}`;

function spokenDate(day, month, year) {
  const d = Number(day);
  const m = Number(month);
  if (!(d >= 1 && d <= 31 && m >= 1 && m <= 12)) return null;
  return `${d === 1 ? '1er' : d} ${MONTHS[m - 1]} ${year}`;
}

// Points dans un nombre : décimale à l'anglaise (« 12.5 » → « 12,5 »), milliers
// (« 1.250.000 » → « 1250000 ») ; une version ou une adresse IP reste telle quelle
function dottedNumber(match) {
  const parts = match.split('.');
  if (parts.length === 2) return `${parts[0]},${parts[1]}`;
  if (/^\d{1,3}$/.test(parts[0]) && parts.slice(1).every((part) => part.length === 3)) return parts.join('');
  return match;
}

/** Syntaxe Markdown en ligne retirée (code, liens, emphase, balises, échappements). */
function inline(text) {
  return text
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/<(?:https?|mailto):[^>\s]+>/gi, 'lien')
    .replace(/\b(?:https?:\/\/|www\.)[^\s<>()\]]+/gi, (url) => `lien${/[.,;:!?]+$/.exec(url)?.[0] ?? ''}`)
    .replace(/\[\^[^\]\s]+\]:?/g, '')
    .replace(/<br\s*\/?>/gi, ', ')
    .replace(/<\/?[a-z][^<>]*>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&(?:#39|apos);/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/(\*\*|__)(\S(?:.*?\S)?)\1/g, '$2')
    .replace(/(^|[^\p{L}\p{N}*\\])\*(\S(?:[^*]*?\S)?)\*(?![\p{L}\p{N}*])/gu, '$1$2')
    .replace(/(^|[^\p{L}\p{N}_\\])_(\S(?:[^_]*?\S)?)_(?![\p{L}\p{N}_])/gu, '$1$2')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/\\([\\`*_{}[\]()#+\-.!|~>])/g, '$1')
    .replace(/^\s*\|\s*|\s*\|\s*$/g, '')
    .replace(/\s*\|\s*/g, ', ')
    .trim();
}

// Pause en fin de ligne : un point, sauf ponctuation déjà présente
const punctuate = (text) => (/[.!?:;…]$/.test(text) ? text : `${text}.`);

function cells(line) {
  let row = line.trim();
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|') && !row.endsWith('\\|')) row = row.slice(0, -1);
  // Barre échappée (« \| ») : reste dans la cellule
  return row.replace(/\\\|/g, '\u0001').split('|').map((cell) => inline(cell.replace(/\u0001/g, '|')));
}

// Ligne de tableau : « colonne : valeur ; colonne : valeur » (cellules vides omises)
function rowSentence(header, row) {
  return row
    .map((value, index) => {
      if (!value) return null;
      const spoken = EMPTY_CELL_RE.test(value) ? 'non renseigné' : value;
      const name = header[index] === '#' ? 'numéro' : header[index];
      return name ? `${name} : ${spoken}` : spoken;
    })
    .filter(Boolean)
    .join(' ; ');
}

// Code (``` ou ~~~ : fermeture de même nature, au moins aussi longue), formules, commentaires
// HTML, flèches et pictogrammes, avant l'analyse ligne à ligne
function prepare(source) {
  const lines = [];
  let fence = null;
  for (const line of source.replace(/\r\n?/g, '\n').split('\n')) {
    const match = FENCE_RE.exec(line);
    if (fence) {
      if (match && match[1][0] === fence[0] && match[1].length >= fence.length && !line.trim().slice(match[1].length).trim()) fence = null;
      continue;
    }
    if (match) {
      fence = match[1];
      lines.push(CODE_BLOCK);
    } else {
      lines.push(line);
    }
  }
  return lines
    .join('\n')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/\$\$[\s\S]*?\$\$/g, ' (formule) ')
    .replace(/[ \t]*[→⇒⟶➜➔][ \t]*/g, ', ')
    .replace(/[↑⬆↗]\u{FE0F}?/gu, ' en hausse ')
    .replace(/[↓⬇↘]\u{FE0F}?/gu, ' en baisse ')
    .replace(PICTOGRAM_RE, '');
}

/** Nombres et unités écrits comme on les prononce (voir l'en-tête du fichier). */
export function spokenUnits(text) {
  return text
    .replace(/[\u{00A0}\u{202F}\u{2007}\u{2009}]/gu, ' ')
    // Dates : « 2026-10-06 », « 06/10/2026 » → « 6 octobre 2026 »
    .replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (match, y, m, d) => spokenDate(d, m, y) ?? match)
    .replace(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/g, (match, d, m, y) => spokenDate(d, m, y) ?? match)
    // Milliers séparés par des espaces : un seul nombre (« 1 250 000 » → « 1250000 »)
    .replace(/\b\d{1,3}(?: \d{3})+\b/g, (match) => match.replace(/ /g, ''))
    .replace(/\b\d+(?:\.\d+)+\b/g, dottedNumber)
    // Heures : « 14h30 », « 14:30 » → « 14 heures 30 » ; « 24h/24 », « 7j/7 »
    .replace(/\b24 ?h ?\/ ?24\b/g, '24 heures sur 24')
    .replace(/\b7 ?j ?\/ ?7\b/g, '7 jours sur 7')
    .replace(/\b([01]?\d|2[0-3])(?::| ?h ?)([0-5]\d)\b/g, (match, h, min) => `${withUnit(h, 'heure', 'heures')}${min === '00' ? '' : ` ${min}`}`)
    // Comparaisons, puis signe moins (« −5 % », « -350 000 ») : « moins 5 pour cent »
    .replace(/≥/g, ' supérieur ou égal à ')
    .replace(/≤/g, ' inférieur ou égal à ')
    .replace(/(^|[\s(])> ?(?=[\d−-])/g, '$1supérieur à ')
    .replace(/(^|[\s(])< ?(?=[\d−-])/g, '$1inférieur à ')
    .replace(/(^|[\s(:;])[−-](?=\d)/g, '$1moins ')
    // Consommation, vitesse, débit
    .replace(/\b[lL] ?\/ ?100 ?km\b/g, 'litres aux 100 kilomètres')
    .replace(/\bkm ?\/ ?h\b/gi, 'kilomètres heure')
    .replace(/\b[lL] ?\/ ?h\b/g, 'litres par heure')
    // Montants : « 12 M FCFA » → « 12 millions de francs CFA », « FCFA » → « francs CFA »
    .replace(/(\d+(?:,\d+)?) ?(Mds?|M|k|K) ?F\.? ?CFA\b/g, (match, n, mult) => `${withUnit(n, ...MULTIPLIERS[mult])} francs CFA`)
    .replace(/(\d) ?(?:F\.? ?CFA|XOF)\b/gi, '$1 francs CFA')
    .replace(/\b(?:F\.? ?CFA|XOF)\b/gi, 'francs CFA')
    // « FCFA/km » → « francs CFA par kilomètre », « L/jour » → « L par jour »
    .replace(/(francs CFA|\bkm|\b[lL]) ?\/ ?(km|[lL]|h|mois|jour|j|an|semaine|véhicule)(?![\p{L}\p{N}])/gu,
      (match, unit, per) => `${unit} par ${PER[per.toLowerCase()]}`)
    // Kilomètres, litres, heures, minutes, points de pourcentage
    .replace(/(\d+(?:,\d+)?) ?km(?![\p{L}\p{N}])/gu, (match, n) => withUnit(n, 'kilomètre', 'kilomètres'))
    .replace(/(^|[^\p{L}\p{N}])km(?![\p{L}\p{N}])/gu, '$1kilomètres')
    .replace(/(\d+(?:,\d+)?) ?[lL](?![\p{L}\p{N}'’])/gu, (match, n) => withUnit(n, 'litre', 'litres'))
    .replace(/\( ?[lL] ?\)/g, '(litres)')
    .replace(/(\d+(?:,\d+)?) ?h(?![\p{L}\p{N}'’])/gu, (match, n) => withUnit(n, 'heure', 'heures'))
    .replace(/(\d+(?:,\d+)?) ?min(?![\p{L}\p{N}])/gu, (match, n) => withUnit(n, 'minute', 'minutes'))
    .replace(/(\d+(?:,\d+)?) ?pts?(?![\p{L}\p{N}])/gu, (match, n) => withUnit(n, 'point', 'points'))
    .replace(/(\d) ?%/g, '$1 pour cent')
    .replace(/(\p{L})%/gu, '$1 pour cent')
    .replace(/%/g, 'pour cent')
    // Abréviations et séparateurs
    .replace(/\b[nN]° ?/g, 'numéro ')
    .replace(/(\d) ?(?:×|\* ) ?(?=\d)/g, '$1 fois ')
    .replace(/ & /g, ' et ')
    .replace(/\bvs\.?(?= )/gi, 'contre')
    .replace(/ [•·—–] /g, ', ');
}

function finalize(text) {
  return spokenUnits(text)
    .replace(/\s+/g, ' ')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')')
    .replace(/\(\s*\)/g, '')
    .replace(/\s+([.,])(?!\d)/g, '$1')
    .replace(/,(\s*[.;:!?])/g, '$1')
    .replace(/([.!?;:])(?:\s*[.;,])+/g, '$1')
    .replace(/^[\s.,;:]+/, '')
    .trim();
}

/** Texte prononçable d'une réponse Markdown (voir l'en-tête du fichier). */
export function speakableText(markdown = '') {
  const lines = prepare(String(markdown ?? '')).split('\n');
  const blocks = [];
  const push = (text) => {
    const clean = inline(text);
    if (clean) blocks.push(punctuate(clean));
  };

  for (let i = 0; i < lines.length; i += 1) {
    const quoted = QUOTE_RE.test(lines[i]);
    let line = unquote(lines[i]);
    if (!line.trim()) continue;

    // Encadré « > [!WARNING] » : son libellé (« Attention : ») introduit le texte
    const callout = quoted ? CALLOUT_RE.exec(line.trim()) : null;
    if (callout) {
      blocks.push(`${CALLOUT_LABELS[callout[1].toLowerCase()]} :`);
      line = line.trim().slice(callout[0].length);
      if (!line.trim()) continue;
    }

    // Tableau : en-tête suivi de la ligne de séparation, puis une phrase par ligne
    if (line.includes('|')) {
      const next = i + 1 < lines.length ? unquote(lines[i + 1]) : '';
      if (next.includes('|') && DELIMITER_RE.test(next)) {
        const header = cells(line);
        let j = i + 2;
        for (; j < lines.length; j += 1) {
          const row = unquote(lines[j]);
          if (!row.trim() || !row.includes('|')) break;
          push(rowSentence(header, cells(row)));
        }
        i = j - 1;
        continue;
      }
      if (DELIMITER_RE.test(line)) continue;
    }

    if (RULE_RE.test(line) || UNDERLINE_RE.test(line)) continue;

    const heading = HEADING_RE.exec(line);
    if (heading) {
      if (heading[1]) push(heading[1]);
      continue;
    }

    const item = ITEM_RE.exec(line);
    if (item) {
      const text = item[2].replace(TASK_RE, '');
      if (text.trim()) push(item[1] ? `${item[1]}. ${text}` : text);
      continue;
    }

    const bold = BOLD_LINE_RE.exec(line.trim());
    push(bold ? `${bold[2]}${bold[3] ? ' :' : ''}` : line);
  }

  return finalize(blocks.join(' '));
}
